import React, { useMemo, useState } from 'react';
import {
  Link as RouterLink,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { format } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';
import { useStatisticsQuery } from '../../hooks/useStatisticsQuery';
import { useSupplierStatsExport } from '../../hooks/useSupplierStatsExport';
import { SupplierStatsExportKind } from '../../services/supplierStatsService';
import {
  StatisticsResponse,
  StatisticsRow,
} from '../../types/supplierStatistics';
import { ProductSortBy, SortOrder } from '../../types/supplierStats';
import { getDatePresets } from '../../utils/datePresets';
import { readProductDetailFilters } from '../../utils/urlState';
import SupplierStatsFiltersBar from './SupplierStatsFiltersBar';
import StatisticsTable, { number } from './StatisticsTable';
import StatisticsNotice from './StatisticsNotice';
import StatisticsTrends from './StatisticsTrends';
import StatisticsDashboard from './StatisticsDashboard';
import StatisticsProductDetail from './StatisticsProductDetail';
import StatisticsProducts from './StatisticsProducts';
import { fetchBrandProductCategories } from '../../services/supplierStatsService';
import { BrandCategoryOption } from '../../types/supplierStats';

const presets = getDatePresets();
const positivePage = (v: string | null) =>
  Math.max(1, Math.floor(Number(v) || 1));
export default function StatisticsPage({
  kind,
}: {
  kind: SupplierStatsExportKind;
}) {
  const { brandName, supplierId: routeSupplier, productId } = useParams();
  const location = useLocation();
  const [query, setQuery] = useSearchParams();
  // Stable defaults prevent a filter-only URL from changing its period while navigating.
  const [defaults] = useState(() => readProductDetailFilters(query));
  const dateFilters = readProductDetailFilters(
    new URLSearchParams({
      startDate:
        query.get('startDate') || format(defaults.startDate, 'yyyy-MM-dd'),
      endDate: query.get('endDate') || format(defaults.endDate, 'yyyy-MM-dd'),
      preset: query.get('preset') || defaults.preset,
    }),
  );
  const { startDate, endDate, preset } = dateFilters;
  const start = format(startDate, 'yyyy-MM-dd'),
    end = format(endDate, 'yyyy-MM-dd');
  const search = query.get('search') || '',
    sortBy = query.get('sortBy') || 'units',
    sortOrder = query.get('sortOrder') === 'ASC' ? 'ASC' : 'DESC';
  const page = positivePage(query.get('page')),
    granularity = query.get('granularity') === 'product' ? 'product' : 'color';
  const stockFilter = query.get('stockFilter') || 'all',
    view = query.get('view') === 'table' ? 'table' : 'cards';
  const categoryId = Number(query.get('categoryId')) || null;
  const supplierRaw = routeSupplier ?? query.get('supplierId');
  const supplierId =
    supplierRaw === null || supplierRaw === undefined
      ? undefined
      : Number(supplierRaw);
  const [refresh, setRefresh] = useState(0);
  const [categories, setCategories] = useState<BrandCategoryOption[]>([]),
    [categoryError, setCategoryError] = useState(false);
  React.useEffect(() => {
    let active = true;
    if (kind === 'brand-products' && brandName) {
      setCategories([]);
      setCategoryError(false);
      fetchBrandProductCategories(brandName)
        .then((r) => {
          if (active) setCategories(r.categories);
        })
        .catch(() => {
          if (active) setCategoryError(true);
        });
    }
    return () => {
      active = false;
    };
  }, [kind, brandName]);
  const path =
    kind === 'brand-products'
      ? `/brands/${encodeURIComponent(brandName || '')}/products`
      : kind === 'product-detail'
        ? `/brands/${encodeURIComponent(brandName || '')}/products/${productId}`
        : kind === 'supplier-brands'
          ? `/suppliers/${routeSupplier}/brands`
          : `/${kind}`;
  const params: Record<string, string | number> = {
    startDate: start,
    endDate: end,
    search,
    page,
    sortBy,
    sortOrder,
    granularity,
    stockFilter,
    ...(categoryId ? { categoryId } : {}),
    ...(supplierId === undefined ? {} : { supplierId }),
  };
  const { data, error, loading } = useStatisticsQuery<StatisticsResponse>(
    path,
    params,
    refresh,
  );
  const update = (
    changes: Record<string, string | number | null>,
    resetPage = true,
  ) => {
    const next = new URLSearchParams(query);
    next.set('startDate', start);
    next.set('endDate', end);
    next.set('preset', preset);
    if (resetPage) next.set('page', '1');
    Object.entries(changes).forEach(([k, v]) => {
      if (v === null || v === '') next.delete(k);
      else next.set(k, String(v));
    });
    setQuery(next, { replace: true });
  };
  const exportOptions = useSupplierStatsExport({
    kind,
    startDate,
    endDate,
    search,
    sortBy: sortBy as ProductSortBy,
    sortOrder: sortOrder as SortOrder,
    granularity,
    categoryId,
    supplierId,
    brandName,
    productId: Number(productId) || undefined,
    stockFilter,
  });
  const currentQuery = useMemo(() => {
    const q = new URLSearchParams(query);
    q.set('startDate', start);
    q.set('endDate', end);
    q.set('preset', preset);
    return q;
  }, [query, start, end, preset]);
  const child = (path: string, extra: Record<string, string> = {}) => {
    const q = new URLSearchParams({
      startDate: start,
      endDate: end,
      preset,
      granularity,
      ...(supplierId === undefined ? {} : { supplierId: String(supplierId) }),
      ...extra,
    });
    q.set('returnTo', `${location.pathname}?${currentQuery}`);
    return `${path}?${q}`;
  };
  const back = (() => {
    const saved = query.get('returnTo');
    if (saved?.startsWith('/supplier-stats/')) {
      const parent = new URL(saved, window.location.origin);
      parent.searchParams.set('startDate', start);
      parent.searchParams.set('endDate', end);
      parent.searchParams.set('preset', preset);
      return `${parent.pathname}?${parent.searchParams}`;
    }
    const q = new URLSearchParams({ startDate: start, endDate: end, preset });
    return kind === 'product-detail'
      ? `/supplier-stats/brands/${encodeURIComponent(brandName || '')}/products?${q}`
      : kind === 'brand-products' && supplierId !== undefined
        ? `/supplier-stats/suppliers/${supplierId}/brands?${q}`
        : kind === 'brand-products'
          ? `/supplier-stats/brands?${q}`
          : kind === 'supplier-brands'
            ? `/supplier-stats/suppliers?${q}`
            : `/supplier-stats/dashboard?${q}`;
  })();
  const detailHref = (r: StatisticsRow) =>
    kind === 'product-detail'
      ? `${location.pathname}?${currentQuery}`
      : child(
          `/supplier-stats/brands/${encodeURIComponent(r.brandName)}/products/${r.productId}`,
        );
  const brandHref = (r: StatisticsRow) =>
    child(`/supplier-stats/brands/${encodeURIComponent(r.brandName)}/products`);
  const supplierHref = (r: StatisticsRow) =>
    child(`/supplier-stats/suppliers/${r.supplierId}/brands`, {
      supplierId: String(r.supplierId),
    });
  const rows = data?.suppliers ?? data?.brands ?? data?.products ?? [];
  const title =
    kind === 'dashboard'
      ? 'Leverantörsstatistik'
      : kind === 'suppliers'
        ? 'Leverantörer'
        : kind === 'brands'
          ? 'Varumärken'
          : kind === 'supplier-brands'
            ? data?.supplier?.supplierName || 'Leverantörens varumärken'
            : kind === 'brand-products'
              ? brandName
              : data?.product?.productName || 'Produktdetalj';
  const table = (
    items: StatisticsRow[],
    level: 'supplier' | 'brand' | 'product',
    compact = false,
  ) => (
    <StatisticsTable
      rows={items}
      level={level}
      href={
        level === 'supplier'
          ? supplierHref
          : level === 'brand'
            ? brandHref
            : detailHref
      }
      compact={compact}
    />
  );
  const trendParams: Record<string, string | number> = {
    startDate: start,
    endDate: end,
    ...(supplierId === undefined ? {} : { supplierId }),
    ...(brandName ? { brandName } : {}),
    ...(productId ? { productId: Number(productId) } : {}),
    ...(kind === 'supplier-brands' && search ? { search } : {}),
  };
  const product = data?.product;
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box
        sx={
          kind === 'product-detail'
            ? {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1,
              }
            : undefined
        }
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          {kind !== 'dashboard' && (
            <Button component={RouterLink} to={back} startIcon={<ArrowBack />}>
              Tillbaka
            </Button>
          )}
          <Box>
            <Typography variant="h4" fontWeight={700}>
              {title}
            </Typography>
            <Typography color="text.secondary">
              {kind === 'product-detail'
                ? `${product?.brandName || brandName || ''} · ${product?.productNumber || 'Produktöversikt'}`
                : 'Försäljning under vald period · Aktuellt lager · Jämförelse föregående år'}
            </Typography>
          </Box>
        </Stack>
        <Stack
          component="nav"
          aria-label="Leverantörsstatistik"
          direction="row"
          gap={1}
          sx={{ mb: 3, flexWrap: 'wrap' }}
        >
          {[
            ['dashboard', 'Översikt'],
            ['suppliers', 'Leverantörer'],
            ['brands', 'Varumärken'],
          ].map(([route, label]) => (
            <Button
              key={route}
              component={RouterLink}
              to={`/supplier-stats/${route}?${new URLSearchParams({ startDate: start, endDate: end, preset, granularity })}`}
              variant={kind === route ? 'contained' : 'text'}
              aria-current={kind === route ? 'page' : undefined}
            >
              {label}
            </Button>
          ))}
        </Stack>
      </Box>
      <SupplierStatsFiltersBar
        presets={presets}
        preset={preset}
        startDate={startDate}
        endDate={endDate}
        onPresetChange={(label) => {
          const range = presets.find((p) => p.label === label)?.getRange();
          update({
            preset: label,
            ...(range
              ? {
                  startDate: format(range.start, 'yyyy-MM-dd'),
                  endDate: format(range.end, 'yyyy-MM-dd'),
                }
              : {}),
          });
        }}
        onStartDateChange={(d) =>
          update({
            startDate: format(d, 'yyyy-MM-dd'),
            preset: 'Anpassad period',
          })
        }
        onEndDateChange={(d) =>
          update({
            endDate: format(d, 'yyyy-MM-dd'),
            preset: 'Anpassad period',
          })
        }
        onRefresh={() => setRefresh((v) => v + 1)}
        onExport={exportOptions.handleExport}
        exporting={exportOptions.exporting}
        exportDisabled={loading || !!error || start > end}
      >
        {kind !== 'product-detail' && kind !== 'dashboard' && (
          <>
            <TextField
              size="small"
              label="Sök"
              value={search}
              onChange={(e) => update({ search: e.target.value })}
            />
            <TextField
              select
              size="small"
              label="Sortering"
              value={sortBy}
              onChange={(e) => update({ sortBy: e.target.value })}
            >
              {[
                ['units', 'Antal¹'],
                ['revenue', 'Omsättning'],
                ['revenueChange', 'Förändring omsättning'],
                ['unitsChange', 'Förändring antal'],
                ['name', 'Namn'],
                ['stock', 'Aktuellt lager'],
                ['retailUnits', 'Butik nettorader'],
                ['onlineUnits', 'Online beställda enheter'],
                ['retailRevenue', 'Butik omsättning'],
                ['onlineRevenue', 'Online omsättning'],
                ['products', 'Produkter'],
                ['brands', 'Varumärken'],
                ['productNumber', 'Artikelnummer'],
              ].map(([v, label]) => (
                <MenuItem key={v} value={v}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Ordning"
              value={sortOrder}
              onChange={(e) => update({ sortOrder: e.target.value })}
            >
              <MenuItem value="DESC">Fallande</MenuItem>
              <MenuItem value="ASC">Stigande</MenuItem>
            </TextField>
          </>
        )}
        {(kind === 'brand-products' || kind === 'dashboard') && (
          <TextField
            select
            size="small"
            label="Produkt-/färgläge"
            value={granularity}
            onChange={(e) => update({ granularity: e.target.value })}
          >
            <MenuItem value="product">Produkter</MenuItem>
            <MenuItem value="color">Färgvarianter</MenuItem>
          </TextField>
        )}
        {kind === 'brand-products' && (
          <>
            <TextField
              select
              size="small"
              label="Lagerfilter"
              value={stockFilter}
              onChange={(e) => update({ stockFilter: e.target.value })}
            >
              <MenuItem value="all">Alla produkter</MenuItem>
              <MenuItem value="inStockNoSales">
                I lager utan försäljning
              </MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Kategori"
              helperText="Aktuellt sortiment"
              value={categoryId ?? ''}
              onChange={(e) => update({ categoryId: e.target.value })}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">Alla kategorier</MenuItem>
              {categories.map((c) => (
                <MenuItem key={c.categoryId} value={c.categoryId}>
                  {c.fullPath || c.categoryName}
                </MenuItem>
              ))}
              {categoryId &&
                !categories.some((c) => c.categoryId === categoryId) && (
                  <MenuItem value={categoryId}>
                    Kategori {categoryId} (underlag saknas)
                  </MenuItem>
                )}
            </TextField>
            <TextField
              select
              size="small"
              label="Visning"
              value={view}
              onChange={(e) => update({ view: e.target.value }, false)}
            >
              <MenuItem value="table">Lista med bilder</MenuItem>
              <MenuItem value="cards">Produktkort</MenuItem>
            </TextField>
          </>
        )}
      </SupplierStatsFiltersBar>
      {categoryError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Kategorier kunde inte hämtas. Vald kategori behålls.
        </Alert>
      )}
      {exportOptions.exportError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={exportOptions.clearExportError}
        >
          {exportOptions.exportError}
        </Alert>
      )}
      {start > end ? (
        <Alert severity="error">
          Slutdatum måste vara samma dag som eller efter startdatum.
        </Alert>
      ) : error ? (
        <Alert
          severity="error"
          action={
            <Button onClick={() => setRefresh((v) => v + 1)}>
              Försök igen
            </Button>
          }
        >
          {error}
        </Alert>
      ) : loading ? (
        <Box role="status" sx={{ py: 5, textAlign: 'center' }}>
          <CircularProgress />
          <Typography>
            Hämtar statistik för {start} – {end}…
          </Typography>
        </Box>
      ) : (
        data && (
          <>
            <StatisticsNotice
              metadata={data.metadata}
              compact={kind === 'product-detail'}
            />
            {kind === 'dashboard' && (
              <StatisticsDashboard
                data={data}
                params={trendParams}
                refresh={refresh}
                detailHref={detailHref}
                brandHref={brandHref}
                supplierHref={supplierHref}
                suppliersHref={child('/supplier-stats/suppliers')}
                brandsHref={child('/supplier-stats/brands')}
              />
            )}
            {kind === 'supplier-brands' && (
              <StatisticsTrends params={trendParams} refresh={refresh} />
            )}
            {kind === 'product-detail' && product && (
              <StatisticsProductDetail
                data={data}
                product={product}
                params={trendParams}
                refresh={refresh}
                detailHref={detailHref}
              />
            )}
            {kind !== 'dashboard' && kind !== 'product-detail' && (
              <>
                <Stack
                  direction="row"
                  gap={1}
                  justifyContent="space-between"
                  alignItems="baseline"
                  sx={{ mb: 2 }}
                >
                  <Typography variant="h6" fontWeight={700}>
                    {kind === 'brand-products'
                      ? 'Produkter'
                      : kind === 'suppliers'
                        ? 'Alla leverantörer'
                        : 'Varumärken'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {number(data.pagination?.total ?? rows.length)} matchande
                    rader
                  </Typography>
                </Stack>
                {kind === 'brand-products' && view === 'cards' ? (
                  <StatisticsProducts rows={rows} href={detailHref} />
                ) : (
                  table(
                    rows,
                    kind === 'suppliers'
                      ? 'supplier'
                      : kind === 'brand-products'
                        ? 'product'
                        : 'brand',
                  )
                )}
                {!rows.length && (
                  <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                    Inga matchande rader. Kontrollera filter och upplysningar om
                    underlaget.
                  </Paper>
                )}
                {data.pagination && (
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mt: 3, gap: 2, flexWrap: 'wrap' }}
                  >
                    <Typography variant="body2">
                      {data.pagination.total} matchande rader · Exporten
                      omfattar alla matchande rader
                    </Typography>
                    <Pagination
                      page={data.pagination.page}
                      count={Math.max(1, data.pagination.totalPages)}
                      onChange={(_, p) => update({ page: p }, false)}
                    />
                  </Stack>
                )}
              </>
            )}
          </>
        )
      )}
    </Container>
  );
}
